/**
 * An invisible row at the end of a scrolling list: when it scrolls into view, the caller loads
 * the next page. `IntersectionObserver` accounts for the clipping scroll container, so the
 * default root is enough. Without the API (old jsdom) the sentinel does nothing.
 */
import { useEffect, useRef } from 'react';

export interface LoadMoreSentinelProps {
    /** False while a page is loading or when there is nothing more to load. */
    active: boolean;
    onVisible: () => void;
}

export function LoadMoreSentinel({ active, onVisible }: LoadMoreSentinelProps) {
    const ref = useRef<HTMLDivElement | null>(null);
    const onVisibleRef = useRef(onVisible);
    onVisibleRef.current = onVisible;

    useEffect(() => {
        const node = ref.current;
        if (!active || !node || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) onVisibleRef.current();
        }, { rootMargin: '120px 0px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [active]);

    return <div ref={ref} aria-hidden='true' className='h-px w-full' data-testid='chat-conversations-sentinel' />;
}
